<template>
  <div id="emailBuilderWrapper">
    <v-row id="bee-plugin-container" />
    <v-row id="integration-container">
      <!--
        ON SAVE MODAL
      -->
      <v-dialog max-width="500px" v-model="showSaveDialog">
        <v-card>
          <v-card-title>
            <span class="text-h5">Save Email Template</span>
          </v-card-title>
          <v-card-text>
            <v-alert
              v-model="showModalError"
              dismissible
              type="error"
              transition="scale-transition"
            >{{ modalError }}</v-alert>
            <v-form @submit.prevent="saveEmailBuilderData">
              <v-text-field
                v-model="emailTemplateName"
                v-validate="'required'"
                data-vv-name="emailTemplateName"
                label="Email Template Name"
                required
              />
              <v-switch
                v-model="emailTemplateOnlyUseR4MergeTags"
                label="Use R4 Merge Tags Only"
              ></v-switch>
              <dealer-select
                v-show="!dealerScopedView"
                v-model="dealer.id"
                v-validate="'required'"
                data-vv-name="dealer_id"
                data-vv-as="Dealer"
                :error-messages="errors.collect('dealer_id')"
                required
              />
              <v-switch
                v-model="isGlobalTemplate"
                label="Global Template"
              ></v-switch>
              <p v-if="dealerScopedView && emailTemplate.is_template">Saving as template</p>
              <p v-else-if="dealerScopedView && emailTemplate.global_template">Global Template Available to all Dealers</p>
              <p v-else>{{ dealer.name }}</p>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click.prevent="saveEmailBuilderData" type="submit">Save</v-btn>
            <v-btn @click.prevent="closeSaveDialog">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!--
        ON SAVE TEMPLATE MODAL
      -->
      <v-dialog max-width="500px" v-model="showTemplateSaveDialog">
        <v-card>
          <v-card-title>
            <span class="text-h5">Save as Starter Template</span>
          </v-card-title>
          <v-card-text>
            <v-alert
              v-model="showModalError"
              dismissible
              type="error"
              transition="scale-transition"
            >{{ modalError }}</v-alert>
            <v-form @submit.prevent="saveEmailTemplateData">
              <v-text-field
                v-model="emailTemplateTemplateName"
                label="Starter Template Name"
                required
              />
              <v-switch
                v-model="emailTemplateOnlyUseR4MergeTags"
                label="Use R4 Merge Tags Only"
              ></v-switch>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click.prevent="saveEmailTemplateData">Save</v-btn>
            <v-btn @click.prevent="closeTemplateSaveDialog">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!--
        ON SEND PREVIEW EMAIL
      -->
      <v-dialog max-width="600px" v-model="showSendPreviewDialog">
        <send-email-preview-dialog
          :dealer-id="dealer.id"
          v-on:close="handleEmailPreviewClose"
          v-on:send="handleEmailPreviewSend"
          v-on:error="handleEmailPreviewError"
          v-on:selected-footer="previewFooterId = $event"
        >
          <template v-slot:header>
            <v-alert
              v-model="showModalError"
              dismissible
              type="error"
              transition="scale-transition"
            >{{ modalError }}</v-alert>
          </template>
        </send-email-preview-dialog>
      </v-dialog>
    </v-row>
    <template>
      <div class="text-center">
        <v-overlay :value="isSendingEmailPreview">
          <p>Sending Email Preview</p>
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </div>
    </template>
    <v-snackbar v-model="showNotice" top>
      {{ noticeMessage }}
      <v-btn color="pink" vertical text @click="doCloseNotice">Close</v-btn>
    </v-snackbar>
  </div> <!-- END emailBuilderWrapper -->
</template>

<script>
import BeePlugin from '@mailupinc/bee-plugin'
import { mapState, mapMutations } from 'vuex'
import { r4Client, EmailTemplateApi } from '@/services'
import { isNil } from 'ramda'
import { DealerSelect, SendEmailPreviewDialog } from '@/components/inputs'
import EmailTemplateCommonMixin from './EmailTemplateCommonMixin'

const TEMPLATE_TYPE = 'BeeFreeEmailTemplate'

function buildCustomFonts () {
  const fontFamilies = [
    'InfinitiMetaBlack-Caps',
    'InfinitiMetaBold',
    'InfinitiMetaBold-Caps',
    'InfinitiMetaBold-ItalicCaps',
    'InfinitiMetaBold-Roman',
    'InfinitiMetaBook',
    'InfinitiMetaBook-Caps',
    'InfinitiMetaBook-ItalicCaps',
    'InfinitiMetaBook-Roman',
    'Jaguar',
    'VW',
    'Mazda'
  ]

  return fontFamilies.map((fFamily) => {
    return {
      name: fFamily,
      fontFamily: fFamily,
      url: 'https://s3.us-east-2.amazonaws.com/dashboard.dealerapps.io/customfonts/stylesheet.css'
    }
  })
}

function emailEditorConfig (uid, mergeTags, specialLinks) {
  return {
    uid: uid, // needed for identify resources of the that user and billing stuff
    container: 'bee-plugin-container', // Identifies the id of div element that contains BEE Plugin
    language: 'en-US',
    preventClose: true,
    mergeTags: mergeTags,
    specialLinks: specialLinks,
    editorFonts: {
      showDefaultFonts: true,
      customFonts: buildCustomFonts()
    },
    onLoad: function (template) {
      this.$logger.trace('BEE: onLoad', template)
      if (this.previewMode) {
        this.emailEditor.preview()
      }
    }.bind(this),
    onSave: function (jsonFile, htmlFile) {
      this.resetModalError()
      this.emailTemplateJson = jsonFile
      this.emailTemplateHtml = htmlFile
      this.showSaveDialog = true
    }.bind(this),
    onSaveAsTemplate: function (jsonFile) {
      this.resetModalError()
      this.emailTemplateTemplateName = '' // reset name from last save
      this.emailTemplateJson = jsonFile // updating both template json and email json
      this.showTemplateSaveDialog = true
    }.bind(this),
    onAutoSave: function (jsonFile) {
      // no autosave at this time
      // too many edge cases
      // what if accidently edit a live template
      // explicit save preferred here
      // in future, autosave to localStorage and then add UI controls
    },
    onSend: function (htmlFile) {
      this.resetModalError()
      this.showSendPreviewDialog = true
      this.emailPreviewHtml = htmlFile
    }.bind(this),
    onError: function (errorMessage) {
      this.$logger.error('BEE ERROR: onError ', errorMessage)
      this.$flash({
        showClose: true,
        message: `Error Initializing Editor! ${errorMessage}. Please reload and try again.`,
        type: 'error',
        duration: 0
      })
    }.bind(this)
  }
}

export default {
  name: 'bee-free-builder',
  templateType: TEMPLATE_TYPE,
  mixins: [EmailTemplateCommonMixin],
  props: {
    emailTemplateStarter: {
      type: [String],
      required: false,
      default: 'blank-template'
    },
    previewMode: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    [DealerSelect.name]: DealerSelect,
    [SendEmailPreviewDialog.name]: SendEmailPreviewDialog
  },
  data: function () {
    return {

    }
  },
  computed: {
    ...mapState('DealerStore', ['dealer']),
    ...mapState('ApplicationStore', ['dealerScopedView']),
    ...mapState('EmailTemplateStore', ['suggestedName'])
  },
  mounted: function () {
    const credentialsPromise = r4Client().post('/api/v1/credentials', {
      token_type: 'bee_token'
    })

    const templatePromise = !isNil(this.emailTemplateId)
      ? EmailTemplateApi.getEmailTemplate(this.emailTemplateId)
      : EmailTemplateApi.getStarterTemplate(this.emailTemplateStarter, this.emailTemplateType)

    const mergeTagsPromise = EmailTemplateApi.getMergeTags()
    const specialLinksPromise = EmailTemplateApi.getSpecialLinks()
    const editorConfigBound = emailEditorConfig.bind(this)

    Promise.all([
      credentialsPromise,
      templatePromise,
      mergeTagsPromise,
      specialLinksPromise
    ])
      .then(
        function (pResults) {
          // ONE FINAL CHECK TO MAKE SURE DEALERS ALIGN
          // NOTE query.dealer_id is a string & dealer.id is an int. using == over ===
          // if not aligned at this point, we have an error case....
          /* eslint-disable eqeqeq */
          if (this.dealer.id != this.$route.query.dealer_id) {
            throw new Error('Dealer ID not set or out of sync!')
          }
          // store the template result...
          this.emailTemplate = pResults[1].data

          const beeEditor = new BeePlugin(pResults[0].data)
          const template = this.emailTemplate.template_json
          const mergeTags = pResults[2].data
          const specialLinks = pResults[3].data
          const beeUserKey = this.dealer.dms_key // Use dms key for id in case id ever changes....
          beeEditor
            .start(
              editorConfigBound(beeUserKey, mergeTags, specialLinks),
              template
            )
            .then(
              function (instance) {
                this.$logger.trace('return instance', instance)
                this.emailEditor = instance
              }.bind(this)
            )
        }.bind(this)
      )
      .catch(
        function (error) {
          this.$logger.error(error.message)
          this.$emit('load-failure', error)
        }.bind(this)
      )
  },
  beforeDestroy () {
    this.$logger.trace('resetting suggested name')
    this.resetSuggestedName()
  },
  methods: {
    ...mapMutations('DealerStore', ['saveDealer'])
  }
}
</script>

<style lang="scss">
$integrationContainerHeight: 0px;

#bee-plugin-container {
  position: absolute;
  top: 0px;
  bottom: $integrationContainerHeight;
  left: 0px;
  right: 0px;
}
#integration-container {
  background-color: #fafafa;
  position: absolute;
  height: $integrationContainerHeight;
  bottom: 0px;
  left: 0px;
  right: 0px;
}
</style>
